import React, {ComponentType, PureComponent} from "react"

import {H1} from "@irati/ui/src//Typography/Heading"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {TenantOnboarding, TenantOnboardingStep, TenantOnboardingStepStates} from "@irati/types"
import OnboardingService from "@irati/onboarding/services/onboarding"
import history from "@irati/utils/history"
import Routes, {SETUP_TENANT} from "@irati-web/app/src/routeConfig"
import Loading from "@irati/ui/src/Loading"
import tracking from "@irati-tracking/tenant-setup"
import {StepAndOptionsParams, StepParams} from "@irati-tracking/tenant-setup/interfaces"

import CompleteStepFooter from "./CompleteStepFooter"
import {getNextStepPath, isValidStep} from "../../helpers"
import OptionContainerProps from "../Steps/sharedProps"
import {Container, Content, Subtitle, SubtitleWrapper} from "./styled"
import {Option} from "../OptionPicker"
import stepDetails, {SetupSteps} from "../../constants/stepConfig"

interface Props extends InjectIntl {
  stepId: SetupSteps,
  title: string,
  subTitle: string,
  selection: number,
  state: TenantOnboardingStepStates,
  StepComponent: ComponentType<OptionContainerProps>,
  tenantOnboarding: TenantOnboarding,
  updateOnboardingSteps: (steps: TenantOnboardingStep[]) => void,
  subTitleMoreText?: string
}

interface State {
  load?: Promise<void>,
  isStepCompletable: boolean
}

interface UpdateStepParameters {
  stepId: string,
  state: TenantOnboardingStepStates,
  selection: number,
  routeOnComplete: boolean
}

class StepContent extends PureComponent<Props, State> {
  state: State = {
    isStepCompletable: false
  }

  componentDidMount() {
    tracking.viewStep(this.getStepDetails())
  }

  getStepDetails = (): StepParams => {
    const {stepId, formatMessage} = this.props
    const step = stepDetails[stepId]
    return {
      step_name: formatMessage({id: step.title}),
      step_id: stepId,
      step_number: step.order
    }
  }

  setIsStepCompletable = (isStepCompletable: boolean) => {
    this.setState({isStepCompletable})
  }

  handleNextStep = () =>
    history.push(`${Routes[SETUP_TENANT]}${getNextStepPath(this.props.stepId, this.props.tenantOnboarding.assignedSteps)}`)

  updateStepSelection = (option: Option) => {
    const {selectionId, title} = option
    const {stepId, state, selection: existingSelection, formatMessage} = this.props
    if (selectionId === existingSelection) return

    const params: StepAndOptionsParams = {
      option_name: formatMessage({id: title}),
      option_id: selectionId,
      ...this.getStepDetails()
    }
    tracking.selectOption(params)
    this.updateStep({stepId, state, selection: selectionId, routeOnComplete: false})
  }

  updateStepStatus = (state: TenantOnboardingStepStates) => {
    const {stepId, state: existingState, selection} = this.props
    if (state === existingState) return

    // CAN BE REMOVED AFTER RESET DEBUF BUTTON REMOVED
    const route = state !== TenantOnboardingStepStates.Open
    // END

    this.updateStep({stepId, state, selection, routeOnComplete: route})

    // Tracking
    const params: StepAndOptionsParams = {
      option_id: selection,
      ...this.getStepDetails()
    }
    if (state === TenantOnboardingStepStates.Skipped) {
      tracking.markStepSkipped(params)
    }
    if (state === TenantOnboardingStepStates.Done) {
      tracking.markStepComplete(params)
    }
    if (stepId === SetupSteps.INVITE && state === TenantOnboardingStepStates.Done) {
      tracking.finishSetup(params)
    }
    tracking.selectOption(params)
  }

  updateStep = (updateObject: UpdateStepParameters) => {
    const {stepId, state, selection, routeOnComplete} = updateObject
    const {updateOnboardingSteps} = this.props
    const load = OnboardingService.updateTenantOnboardingStep(stepId, state, selection).then(
      async(updatedOnboarding: TenantOnboarding) => {
        await updateOnboardingSteps(updatedOnboarding.assignedSteps.filter(isValidStep))
        if (routeOnComplete) {
          const nextStepPath = getNextStepPath(stepId, updatedOnboarding.assignedSteps)
          if (nextStepPath) {
            history.push(`${Routes[SETUP_TENANT]}${nextStepPath}`)
          }
        }
      }
    )
    this.setState({load})
  }

  render() {
    const {stepId, title, subTitle, subTitleMoreText, state, StepComponent, selection, tenantOnboarding} = this.props
    const {load, isStepCompletable} = this.state

    return (
      <Container>
        <Content>
          <H1 tiny>
            <FormattedMessage id={title} />
          </H1>
          <SubtitleWrapper>
            <Subtitle base>
              <FormattedMessage id={subTitle} />
            </Subtitle>
            {subTitleMoreText &&
              <Subtitle marginTop={2} base>
                <FormattedMessage id={subTitleMoreText} />
              </Subtitle>
            }
          </SubtitleWrapper>

          <Loading load={load}>
            <StepComponent
              setIsStepCompletable={this.setIsStepCompletable}
              existingSelectionId={selection}
              handleOptionChange={this.updateStepSelection}
              tenantOnboarding={tenantOnboarding} />
          </Loading>
        </Content>
        <CompleteStepFooter
          stepId={stepId}
          completable={isStepCompletable}
          state={state}
          handleNextStep={this.handleNextStep}
          handleMarkComplete={() => this.updateStepStatus(TenantOnboardingStepStates.Done)}
          handleSkip={() => this.updateStepStatus(TenantOnboardingStepStates.Skipped)} />
      </Container>
    )
  }
}

export default injectIntl(StepContent)
